import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { sectorOptionsForType, SWISS_CANTONS, EMPLOYEE_TIER_OPTIONS, SOURCE_OPTIONS, TIMELINE_OPTIONS } from '../config/leadTaxonomy';
import type { Lead, NewLeadInput, Sector, EmployeeTier, LeadSource, EstimatedTimeline, SectorType, PreferredLanguage } from '../types/lead.types';
import { createLead, updateLead } from '../services/leadService';
import { listTeamMembers, type TeamMember } from '../services/teamService';
import { toDateInputValue, fromDateInputValue } from '../utils/date';

interface Props {
  createdBy: string;
  /** Presente = modalità modifica di un lead esistente. Assente = creazione. */
  lead?: Lead;
  onClose: () => void;
  onSaved: () => void;
}

const emptyForm: NewLeadInput = {
  organizationName: '',
  sectorType: 'private',
  sector: 'other',
  employeeTier: 'micro',
  contactName: '',
  email: '',
  preferredLanguage: 'it',
  source: 'referral',
  ownerId: '',
};

function formFromLead(lead: Lead): NewLeadInput {
  const { id, stage, stageHistory, createdAt, updatedAt, createdBy, convertedMandateId, onboardingToken, ...rest } = lead;
  return rest;
}

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">{label}</label>
      {children}
    </div>
  );
}

export function LeadFormModal({ createdBy, lead, onClose, onSaved }: Props) {
  const isEditing = !!lead;
  const [form, setForm] = useState<NewLeadInput>(lead ? formFromLead(lead) : { ...emptyForm, ownerId: createdBy });
  const [followUpDate, setFollowUpDate] = useState(lead ? toDateInputValue(lead.nextFollowUpAt) : '');
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    listTeamMembers().then(setTeam).catch(() => setTeam([]));
  }, []);

  // Cambiando Privato/Pubblico il settore corrente potrebbe non appartenere
  // più alla lista visibile — lo si riporta al primo valore valido del nuovo
  // tipo, mai lasciato "orfano" su un'opzione nascosta.
  const handleSectorTypeChange = (sectorType: SectorType) => {
    const options = sectorOptionsForType(sectorType);
    setForm({ ...form, sectorType, sector: options[0]?.id ?? form.sector });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const nextFollowUpAt = fromDateInputValue(followUpDate);
      // Un lead trovato dall'agente di sourcing può arrivare qui senza
      // dimensione (mai fornita da Zefix) — la prima modifica manuale la
      // fissa a un valore reale, mai lasciata undefined (il server la richiede esplicita).
      const payload = { ...form, employeeTier: form.employeeTier ?? 'micro', nextFollowUpAt };
      if (isEditing) {
        await updateLead(lead.id, payload);
      } else {
        await createLead(payload, createdBy);
      }
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  const visibleSectors = sectorOptionsForType(form.sectorType);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <form
        onSubmit={handleSubmit}
        className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl p-6 space-y-3 max-h-[90vh] overflow-y-auto"
      >
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
          <X size={20} />
        </button>
        <h2 className="text-lg font-bold text-navy-900">{isEditing ? 'Modifica lead' : 'Nuovo lead'}</h2>

        <Field label="Ragione sociale / nome ente">
          <input
            required
            value={form.organizationName}
            onChange={(e) => setForm({ ...form, organizationName: e.target.value })}
            className={inputClass}
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Tipo">
            <select
              value={form.sectorType}
              onChange={(e) => handleSectorTypeChange(e.target.value as SectorType)}
              className={inputClass}
            >
              <option value="private">Privato</option>
              <option value="public">Pubblico</option>
            </select>
          </Field>
          <Field label="Settore">
            <select
              value={form.sector}
              onChange={(e) => setForm({ ...form, sector: e.target.value as Sector })}
              className={inputClass}
            >
              {visibleSectors.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Dimensione">
            <select
              value={form.employeeTier ?? 'micro'}
              onChange={(e) => setForm({ ...form, employeeTier: e.target.value as EmployeeTier })}
              className={inputClass}
            >
              {EMPLOYEE_TIER_OPTIONS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Cantone">
            <select
              value={form.canton ?? ''}
              onChange={(e) => setForm({ ...form, canton: e.target.value || undefined })}
              className={inputClass}
            >
              <option value="">Non specificato</option>
              {SWISS_CANTONS.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.id})</option>)}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Nome contatto">
            <input
              placeholder="Da individuare"
              value={form.contactName ?? ''}
              onChange={(e) => setForm({ ...form, contactName: e.target.value || undefined })}
              className={inputClass}
            />
          </Field>
          <Field label="Ruolo (es. DPO, direzione)">
            <input
              value={form.contactRole ?? ''}
              onChange={(e) => setForm({ ...form, contactRole: e.target.value })}
              className={inputClass}
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Email">
            <input
              type="email"
              value={form.email ?? ''}
              onChange={(e) => setForm({ ...form, email: e.target.value || undefined })}
              className={inputClass}
            />
          </Field>
          <Field label="Telefono">
            <input
              value={form.phone ?? ''}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className={inputClass}
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Lingua preferita">
            <select
              value={form.preferredLanguage}
              onChange={(e) => setForm({ ...form, preferredLanguage: e.target.value as PreferredLanguage })}
              className={inputClass}
            >
              <option value="it">IT</option>
              <option value="de">DE</option>
              <option value="fr">FR</option>
              <option value="en">EN</option>
            </select>
          </Field>
          <Field label="Fonte">
            <select
              value={form.source}
              onChange={(e) => setForm({ ...form, source: e.target.value as LeadSource })}
              className={inputClass}
            >
              {SOURCE_OPTIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Timeline stimata">
            <select
              value={form.estimatedTimeline ?? ''}
              onChange={(e) => setForm({ ...form, estimatedTimeline: e.target.value as EstimatedTimeline || undefined })}
              className={inputClass}
            >
              <option value="">Non specificata</option>
              {TIMELINE_OPTIONS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Valore stimato (CHF)">
            <input
              type="number"
              value={form.estimatedDealValue ?? ''}
              onChange={(e) => setForm({ ...form, estimatedDealValue: e.target.value ? Number(e.target.value) : undefined })}
              className={inputClass}
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Prossimo follow-up">
            <input
              type="date"
              value={followUpDate}
              onChange={(e) => setFollowUpDate(e.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="Proprietario">
            <select
              value={form.ownerId}
              onChange={(e) => setForm({ ...form, ownerId: e.target.value })}
              className={inputClass}
            >
              {team.length === 0 && <option value={form.ownerId}>Io</option>}
              {team.map((m) => <option key={m.uid} value={m.uid}>{m.displayName}</option>)}
            </select>
          </Field>
        </div>

        <Field label="Motivo del contatto (audit, breach, richiesta cliente...)">
          <textarea
            value={form.painPoint ?? ''}
            onChange={(e) => setForm({ ...form, painPoint: e.target.value })}
            className={inputClass}
            rows={2}
          />
        </Field>

        <button
          type="submit" disabled={saving}
          className="w-full py-2 rounded-lg text-white font-semibold bg-navy-900 disabled:opacity-50"
        >
          {saving ? 'Salvataggio…' : isEditing ? 'Salva modifiche' : 'Crea lead'}
        </button>
      </form>
    </div>
  );
}
